import React, { useEffect, useState } from 'react';
import { COLORMAPS, colormapGradient, formatRasterValue } from '../../lib/raster-api';
import { probeRows } from '../../hooks/useRasterLayers';
import { DEM_PRODUCT_LABELS } from '../../lib/terrain-api';

/**
 * Floating panel for raster dataset layers (hooks/useRasterLayers): visibility,
 * opacity, colormap and value range (single-band), zoom, remove, the pixel probe and
 * the processing / error state. Renders nothing without a raster.
 */

const panel = {
  position: 'absolute', left: 10, bottom: 44, zIndex: 6, width: 270, maxHeight: '55%', overflowY: 'auto',
  padding: 10, borderRadius: 8, fontSize: 12, color: '#e6edf3',
  background: 'rgba(13,17,23,0.92)', border: '1px solid rgba(255,255,255,0.1)',
};
const btn = {
  fontSize: 11, padding: '2px 8px', borderRadius: 6, cursor: 'pointer', color: '#00d4ff',
  background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.25)',
};
const input = {
  width: 70, fontSize: 11, padding: '2px 4px', borderRadius: 4, color: '#e6edf3',
  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
};

export function RangeInput({ value, onCommit, label }) {
  const [text, setText] = useState(value ?? '');
  useEffect(() => { setText(value ?? ''); }, [value]);
  const commit = () => {
    const n = text === '' ? null : Number(text);
    if (n === null || Number.isFinite(n)) onCommit(n);
    else setText(value ?? '');
  };
  return (
    <input aria-label={label} style={input} value={text} onChange={(e) => setText(e.target.value)}
      onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') commit(); }} />
  );
}

/** True while there is something to show (a raster layer or a load in flight). */
export function rasterControlsVisible(raster) {
  return !!raster && !(raster.status === 'idle' && !raster.rasterLayers.length);
}

// `embedded`: rendered inside a dock / floating window (components/map/DockLayout.js), which
// owns the position and size, instead of absolutely positioned over the map.
const embeddedPanel = { position: 'static', width: 'auto', maxHeight: 'none', border: 'none', borderRadius: 0, minHeight: '100%' };

export default function RasterLayerControls({ raster, embedded = false }) {
  if (!rasterControlsVisible(raster)) return null;
  const { rasterLayers, status, error, probeOn, setProbeOn, probe, clearProbe,
    setLayerStyle, setOpacity, toggleLayer, removeLayer, zoomTo, setContours } = raster;
  return (
    <div style={embedded ? { ...panel, ...embeddedPanel } : panel} data-testid="raster-controls">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <strong>Rasters</strong>
        {rasterLayers.length > 0 && (
          <button style={{ ...btn, ...(probeOn ? { background: 'rgba(0,212,255,0.35)' } : {}) }}
            onClick={() => { setProbeOn(!probeOn); if (probeOn) clearProbe(); }} data-testid="raster-probe-toggle"
            title="Click the map to read pixel values">
            {probeOn ? 'Probe on' : 'Probe values'}
          </button>
        )}
      </div>
      {status === 'loading' && <div>Loading raster…</div>}
      {status === 'processing' && <div>Processing raster… progress is in the task console.</div>}
      {error && <div role="alert" style={{ color: '#f87171', marginBottom: 6 }}>{String(error)}</div>}
      {rasterLayers.map((l) => {
        const single = (l.style.bands || []).length === 1 && (!l.style.product || l.style.product === 'elevation');
        return (
          <div key={l.id} style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 6, marginTop: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={l.visible} onChange={() => toggleLayer(l.id)} aria-label={`Show ${l.name}`} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.name}>{l.name}</span>
              <button style={btn} onClick={() => zoomTo(l.id)} title="Zoom to raster">Zoom</button>
              <button style={btn} onClick={() => removeLayer(l.id)} title="Remove layer">×</button>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
              Opacity
              <input type="range" min={0} max={1} step={0.05} value={l.opacity} style={{ flex: 1 }}
                onChange={(e) => setOpacity(l.id, Number(e.target.value))} aria-label="Opacity" />
              <span style={{ width: 30, textAlign: 'right' }}>{Math.round(l.opacity * 100)}%</span>
            </label>
            {l.dem && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }} data-testid="dem-product-controls">
                DEM
                <select value={l.style.product || 'elevation'} style={{ ...input, width: 'auto', flex: 1 }} aria-label="DEM product"
                  onChange={(e) => setLayerStyle(l.id, { product: e.target.value })}>
                  {['elevation', 'hillshade', 'slope', 'aspect'].map((p) => <option key={p} value={p}>{DEM_PRODUCT_LABELS[p]}</option>)}
                </select>
                {setContours && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <input type="checkbox" checked={!!l.showContours} onChange={(e) => setContours(l.id, e.target.checked)} />
                    Contours
                  </label>
                )}
              </div>
            )}
            {single && (
              <>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                  Colormap
                  <select value={l.style.colormap || 'viridis'} style={{ ...input, width: 'auto', flex: 1 }}
                    onChange={(e) => setLayerStyle(l.id, { colormap: e.target.value })} aria-label="Colormap">
                    {COLORMAPS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <div style={{ height: 8, borderRadius: 3, marginTop: 6, background: colormapGradient(l.style.colormap) }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, gap: 6 }}>
                  <RangeInput label="Minimum" value={l.style.vmin} onCommit={(v) => setLayerStyle(l.id, { vmin: v })} />
                  <RangeInput label="Maximum" value={l.style.vmax} onCommit={(v) => setLayerStyle(l.id, { vmax: v })} />
                </div>
              </>
            )}
          </div>
        );
      })}
      {probe && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 6, marginTop: 6 }} data-testid="raster-probe">
          <div style={{ color: '#8b949e' }}>{probe.layerName} @ {probe.lng.toFixed(6)}, {probe.lat.toFixed(6)}</div>
          {probe.loading && <div>Reading…</div>}
          {probe.error && <div style={{ color: '#f87171' }}>{probe.error}</div>}
          {probe.result && !probe.result.inside && <div>Outside the raster</div>}
          {probe.result?.inside && probeRows(probe.result).map((r) => (
            <div key={r.band}>Band {r.band}: <strong>{r.value === null ? 'NoData' : formatRasterValue(r.value)}</strong></div>
          ))}
        </div>
      )}
    </div>
  );
}
